import { fmtDate } from "utils/date";
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Badge,
  Spinner,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { ReporteService } from "service/ReporteService";
import { CicloService } from "service/CicloService";
import FSelect from "components/Select/FSelect";
import FDateInput from "components/DateInput/FDateInput";

const reporteService = new ReporteService();
const cicloService = new CicloService();

function ReporteAsistencia() {
  const notificationAlertRef = useRef(null);

  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const today = new Date().toISOString().split("T")[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];

  const [modo, setModo] = useState("general"); // "general" | "alumno"

  // General mode
  const [listCiclos, setListCiclos] = useState([]);
  const [cicloSeleccionado, setCicloSeleccionado] = useState("");
  const [fechaDesde, setFechaDesde] = useState(thirtyDaysAgo);
  const [fechaHasta, setFechaHasta] = useState(today);
  const [listAsistencia, setListAsistencia] = useState([]);

  // Alumno mode
  const [dni, setDni] = useState("");
  const [listSesiones, setListSesiones] = useState([]);
  const [alumnoInfo, setAlumnoInfo] = useState(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCiclos();
  }, []);

  const getCiclos = async () => {
    try {
      const data = await cicloService.getCiclos();
      const list = Array.isArray(data) ? data : data?.data || [];
      setListCiclos(list);
      if (list.length > 0) {
        setCicloSeleccionado(list[0].tipociclo || list[0]._id);
      }
    } catch (error) {
      console.error("Error cargando ciclos:", error);
    }
  };

  const buscarGeneral = async () => {
    if (!cicloSeleccionado) {
      notify("warning", "Selecciona un ciclo antes de buscar.");
      return;
    }
    setLoading(true);
    setListAsistencia([]);
    try {
      const data = await reporteService.getAsistenciaAlumnos(
        cicloSeleccionado,
        fechaDesde,
        fechaHasta
      );
      const list = Array.isArray(data) ? data : data?.data || [];
      setListAsistencia(list);
      if (list.length === 0) notify("warning", "No se encontraron registros para los filtros seleccionados.");
      else notify("success", `${list.length} registro(s) encontrado(s).`);
    } catch (error) {
      console.error("Error en reporte general:", error);
      notify("danger", "Error al obtener el reporte. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const buscarPorDni = async () => {
    if (!dni || dni.length < 8) {
      notify("warning", "Ingresa un DNI válido de 8 dígitos.");
      return;
    }
    setLoading(true);
    setListSesiones([]);
    setAlumnoInfo(null);
    try {
      const dataAlumno = await reporteService.getSesionAlumno(dni);
      const alumno = Array.isArray(dataAlumno)
        ? dataAlumno[0]
        : dataAlumno?.data?.[0] || dataAlumno;

      if (!alumno) {
        notify("warning", "No se encontró ningún alumno con ese DNI.");
        return;
      }

      setAlumnoInfo(alumno);

      const usuarioData = await reporteService.getUsuarioAlumno(alumno._id);
      const usuario = Array.isArray(usuarioData)
        ? usuarioData[0]
        : usuarioData?.data?.[0] || usuarioData;

      if (!usuario) {
        notify("warning", "El alumno no tiene usuario registrado en el sistema.");
        return;
      }

      const sesionesData = await reporteService.getAsistencias(usuario._id, fechaDesde, fechaHasta);
      const sesiones = Array.isArray(sesionesData)
        ? sesionesData
        : sesionesData?.data || [];
      setListSesiones(sesiones);
      if (sesiones.length === 0) notify("warning", "El alumno no tiene sesiones registradas en el rango de fechas.");
      else notify("success", `${sesiones.length} sesión(es) encontrada(s).`);
    } catch (error) {
      console.error("Error buscando alumno:", error);
      notify("danger", "Error al buscar el alumno. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <NotificationAlert ref={notificationAlertRef} />
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col md="8">
                  <CardTitle tag="h4">Reporte de Asistencia</CardTitle>
                </Col>
                <Col md="4" className="text-right">
                  <Button
                    color="link"
                    className="btn-icon btn-round"
                    title="Configuración"
                  >
                    <i className="tim-icons icon-settings-gear-63" />
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {/* Selector de modo */}
              <Row className="mb-3">
                <Col md="12">
                  <FormGroup check inline className="mb-0">
                    <Label check>
                      <Input
                        type="radio"
                        name="modo"
                        value="general"
                        checked={modo === "general"}
                        onChange={() => setModo("general")}
                      />{" "}
                      Reporte General (por Ciclo)
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="mb-0">
                    <Label check>
                      <Input
                        type="radio"
                        name="modo"
                        value="alumno"
                        checked={modo === "alumno"}
                        onChange={() => setModo("alumno")}
                      />{" "}
                      Por Alumno (DNI)
                    </Label>
                  </FormGroup>
                </Col>
              </Row>

              {/* Zona 2: Barra de filtros */}
              <Row className="align-items-end mb-4">
                {modo === "general" ? (
                  <>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Label>Ciclo</Label>
                        <FSelect
                          value={cicloSeleccionado}
                          onChange={(e) => setCicloSeleccionado(e.target.value)}
                          options={listCiclos.map((c) => ({ value: c.tipociclo || c._id, label: c.tipociclo || c.denominacion }))}
                          placeholder="Seleccionar ciclo"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Label>Fecha Desde</Label>
                        <FDateInput
                          value={fechaDesde}
                          onChange={(e) => setFechaDesde(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Label>Fecha Hasta</Label>
                        <FDateInput
                          value={fechaHasta}
                          onChange={(e) => setFechaHasta(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="1">
                      <FormGroup className="mb-0">
                        <Button
                          color="info"
                          block
                          onClick={buscarGeneral}
                          disabled={loading}
                          style={{ height: "40px", padding: "0" }}
                          title="Buscar"
                        >
                          {loading ? <Spinner size="sm" /> : <i className="tim-icons icon-zoom-split" />}
                        </Button>
                      </FormGroup>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Label>DNI del alumno</Label>
                        <Input
                          type="text"
                          placeholder="DNI (8 dígitos)"
                          value={dni}
                          maxLength="8"
                          onChange={(e) => setDni(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && buscarPorDni()}
                          style={{ height: "40px" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Label>Fecha Desde</Label>
                        <FDateInput
                          value={fechaDesde}
                          onChange={(e) => setFechaDesde(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Label>Fecha Hasta</Label>
                        <FDateInput
                          value={fechaHasta}
                          onChange={(e) => setFechaHasta(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="1">
                      <FormGroup className="mb-0">
                        <Button
                          color="info"
                          block
                          onClick={buscarPorDni}
                          disabled={loading}
                          style={{ height: "40px", padding: "0" }}
                          title="Buscar"
                        >
                          {loading ? <Spinner size="sm" /> : <i className="tim-icons icon-zoom-split" />}
                        </Button>
                      </FormGroup>
                    </Col>
                  </>
                )}
              </Row>

              {/* Zona 3: Barra de acciones */}
              <Row className="mb-4">
                <Col md="6">
                  {modo === "alumno" && alumnoInfo && (
                    <div className="p-2 border rounded d-inline-block">
                      <strong>{`${alumnoInfo.apellidopaterno} ${alumnoInfo.apellidomaterno}, ${alumnoInfo.nombres}`}</strong>{" "}
                      <Badge color="info" className="ml-2">{alumnoInfo.documentoidentidad}</Badge>
                    </div>
                  )}
                </Col>
                <Col md="6" className="text-right">
                  {/* Espacio para acciones adicionales */}
                </Col>
              </Row>

              <hr className="my-4" />

              {/* Tabla modo general */}
              {modo === "general" && listAsistencia.length > 0 && (
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>DNI</th>
                      <th>Apellidos y Nombres</th>
                      <th>Asistencias</th>
                      <th>Última Sesión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listAsistencia.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{row.documentoidentidad}</td>
                        <td>
                          {row.apellidopaterno} {row.apellidomaterno},{" "}
                          {row.nombres}
                        </td>
                        <td>{row.totalsesiones ?? "-"}</td>
                        <td>
                          {row.fechaultimasesion
                            ? new Date(row.fechaultimasesion).toLocaleString(
                                "es-PE"
                              )
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {/* Tabla modo alumno */}
              {modo === "alumno" && listSesiones.length > 0 && (
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>Fecha Creación</th>
                      <th>Última Sesión</th>
                      <th>Navegador</th>
                      <th>Sistema Operativo</th>
                      <th>Dispositivo</th>
                      <th>Total Sesiones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listSesiones.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>
                          {fmtDate(row.fechacreacion)}
                        </td>
                        <td>
                          {row.fechaultimasesion
                            ? new Date(row.fechaultimasesion).toLocaleString(
                                "es-PE"
                              )
                            : "-"}
                        </td>
                        <td>{row.navegadorultima || "-"}</td>
                        <td>{row.soultima || "-"}</td>
                        <td>{row.tipodispositivoultima || "-"}</td>
                        <td>{row.totalsesiones ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
    </>
  );
}

export default ReporteAsistencia;
